import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useEffect, useMemo } from 'react';

import type {
  Sport,
  SearchFacetDefinitionInput,
  SearchRefinementInput,
  SearchSortInput,
} from '@sorare/core/src/__generated__/globalTypes';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { isLiveMatchdayPick } from '@sorare/core/src/hooks/market/useMatchdayPicksTab';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { useTimeLeft } from '@sorare/core/src/hooks/useTimeLeft';

import { MatchdayPickCard } from '#src/components/matchdayPicks/MatchdayPickCard/index.tsx';

import type {
  MatchdayPicksQuery,
  MatchdayPicksQueryVariables,
} from './__generated__/useMatchdayPicks.graphql.ts';

const MATCHDAY_PICKS_PAGE_SIZE = 20;

// `flashSalePicks` returns the campaigns rather than card hits: every lock, stock and sold-out
// signal on the tile hangs off `campaign.primaryOffer`, and a card hit's `livePrimaryOffer` is null
// the moment a pick sells, so hits cannot render a sold-out tile.
//
// `activeSteppedDynamicCampaigns` stays alongside it, unfiltered, to tell a sale that is live from
// no sale at all.
const MATCHDAY_PICKS_QUERY = gql`
  query MatchdayPicksQuery(
    $sport: Sport
    $query: String
    $page: Int
    $pageSize: Int
    $advancedFilters: String
    $refinements: [SearchRefinementInput!]
    $facets: [SearchFacetDefinitionInput!]
    $sorts: [SearchSortInput!]
    $googlePlayStoreCountryCode: String
    $isAndroidApp: Boolean = false
    $isIosApp: Boolean = false
    $withCollectionPredictions: Boolean = false
  ) {
    market {
      id
      flashSalePicks(
        sport: $sport
        query: $query
        page: $page
        pageSize: $pageSize
        advancedFilters: $advancedFilters
        refinements: $refinements
        facets: $facets
        sorts: $sorts
      ) {
        page
        nbPages
        nbHits
        facets {
          field
          values {
            value
            count
          }
          min {
            value
          }
          max {
            value
          }
        }
        campaigns {
          id
          endDate
          rarity
          player {
            slug
            displayName
          }
          primaryOffer {
            id
          }
          ...MatchdayPickCard_instantBuyCampaign
        }
      }
      activeSteppedDynamicCampaigns {
        nodes {
          id
          player {
            slug
          }
        }
      }
    }
  }
  ${MatchdayPickCard.fragments.instantBuyCampaign}
` as TypedDocumentNode<MatchdayPicksQuery, MatchdayPicksQueryVariables>;

type Campaign = NonNullable<
  MatchdayPicksQuery['market']
>['flashSalePicks']['campaigns'][number];

export type MatchdayPick = Campaign & {
  player: NonNullable<Campaign['player']>;
  primaryOffer: NonNullable<Campaign['primaryOffer']>;
};

type Renderable = {
  player?: { slug: string } | null;
  primaryOffer?: { id: string } | null;
};

const isRenderable = <T extends Renderable>(campaign: T) =>
  !!campaign.player && !!campaign.primaryOffer;

const soonestEndDateOf = (campaigns: { endDate: Date }[]) =>
  campaigns.reduce<Date | null>(
    (soonest, { endDate }) =>
      !soonest || endDate < soonest ? endDate : soonest,
    null
  );

type Props = {
  sport: Sport;
  query: string;
  page: number;
  advancedFilters: string;
  refinements: SearchRefinementInput[];
  facets: SearchFacetDefinitionInput[];
  sorts: SearchSortInput[];
  skip?: boolean;
};

export const useMatchdayPicks = ({
  sport,
  query,
  page,
  advancedFilters,
  refinements,
  facets,
  sorts,
  skip,
}: Props) => {
  const { isIosApp, isAndroidApp, googlePlayStoreCountryCode } =
    useIsMobileApp();

  const { data, previousData, loading, refetch } = useQuery(
    MATCHDAY_PICKS_QUERY,
    {
      variables: {
        sport,
        query,
        page,
        pageSize: MATCHDAY_PICKS_PAGE_SIZE,
        advancedFilters,
        refinements,
        facets,
        sorts,
        isIosApp,
        isAndroidApp,
        googlePlayStoreCountryCode,
      },
      skip,
    }
  );

  const market = (data ?? previousData)?.market;
  const flashSalePicks = market?.flashSalePicks;

  const picks = useMemo(
    () => (flashSalePicks?.campaigns || []).filter(isRenderable),
    [flashSalePicks?.campaigns]
  ) as MatchdayPick[];

  const liveCampaigns = useMemo(
    () =>
      (market?.activeSteppedDynamicCampaigns.nodes || []).filter(
        isLiveMatchdayPick
      ),
    [market?.activeSteppedDynamicCampaigns.nodes]
  );

  // Nothing else refreshes the grid, so a pick whose sale has ended keeps its buy action until the
  // query re-runs.
  const soonestPickEnd = useMemo(() => soonestEndDateOf(picks), [picks]);
  const { isEnded: aPickHasEnded } = useTimeLeft(soonestPickEnd);

  useEffect(() => {
    if (aPickHasEnded) refetch();
  }, [aPickHasEnded, refetch]);

  return {
    picks,
    nbHits: flashSalePicks?.nbHits ?? 0,
    nbPages: flashSalePicks?.nbPages ?? 0,
    page: flashSalePicks?.page ?? page,
    facets: flashSalePicks?.facets ?? [],
    liveCampaignCount: liveCampaigns.length,
    loading,
    refetch,
  };
};
